<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="notifications_sidebar-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-toolbar flat height="66" class="notifications_sidebar-toolbar">
        <v-toolbar-title class="notifications_sidebar-toolbar-title"
          >Notifications</v-toolbar-title
        >
        <v-btn
          v-if="isAdvisor"
          rounded
          text
          color="bhBlue1"
          elevation="0"
          @click="$emit('open-settings')"
        >
          <v-icon>bhi-cog</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!isAdvisor"
          color="accent"
          class="readall"
          text
          elevation="0"
          @click="readAll"
          ><v-icon>bhi-double-check</v-icon> Read all</v-btn
        >
        <v-btn color="accent" icon @click="closeDialog">
          <v-icon size="24">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container class="notifications_sidebar-card-container">
        <v-row>
          <v-col sm="12" v-if="notifications.items && !isAdvisor">
            <notification
              v-for="notification in notifications.items"
              :key="notification.id"
              :notification="notification"
              v-on="$listeners"
            />
          </v-col>

          <v-col sm="12" v-if="notifications.content && isAdvisor">
            <v-tabs
              class="page-notifications-grouped-tabs"
              v-model="tab"
              fixed-tabs
              @change="loadNotificationsGrouped"
            >
              <v-tabs-slider></v-tabs-slider>
              <v-tab :disabled="additionalTab === 1">All time</v-tab>
              <v-tab :disabled="additionalTab === 1">3 days</v-tab>
              <v-tab :disabled="additionalTab === 1">Last 24h</v-tab>
            </v-tabs>
            <v-tabs
              class="page-notifications-grouped-tabs"
              v-model="additionalTab"
              fixed-tabs
              @change="loadNotificationsGrouped"
            >
              <v-tabs-slider></v-tabs-slider>
              <v-tab>All</v-tab>
              <v-tab>Unread</v-tab>
            </v-tabs>
            <v-tabs-items
              class="page-notifications-grouped-v-tabs-items"
              v-model="tab"
              v-for="notification in notifications.content"
              :key="notification.profile.profileId"
            >
              <div class="page-notifications-grouped-card-container">
                <notification
                  :key="notification.profile.profileId"
                  :notification="notification"
                  :expand="notification.notifications.length > 1 && isAdvisor"
                  :tab="tab"
                />
              </div>
            </v-tabs-items>
            <div
              class="page-notifications-grouped--empty"
              v-if="notifications.content.length === 0"
            >
              <span class="icon">
                <img src="@/assets/empty-notification-bell.png" />
              </span>
              <h3 class="page-notifications-grouped--empty-title">
                You don't have any notifications that match your filters
              </h3>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="notifications_sidebar-card-footer">
        <v-btn
          v-if="!isAdvisor"
          rounded
          text
          color="bhBlue1"
          elevation="0"
          @click="$emit('open-settings')"
        >
          <v-icon>bhi-cog</v-icon>
          Settings
        </v-btn>
        <v-btn
          v-if="isAdvisor"
          color="accent"
          class="readall"
          text
          elevation="0"
          @click="readAll"
          ><v-icon>bhi-double-check</v-icon> Mark all as read</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn
          @click="
            isAdvisor
              ? $router.push({
                  name: 'NotificationsGrouped',
                  path: '/notifications-grouped',
                })
              : $router.push({ name: 'Notifications', path: '/notifications' })
          "
          rounded
          elevation="0"
          color="accent"
          >Show all</v-btn
        >
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Notification from '@/components/notifications/Notification.vue'

export default {
  name: 'NotificationsDialog',
  components: {
    Notification,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      tab: null,
      additionalTab: null,
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
      } else {
        this.dialog = false
      }
    },
    async additionalTab() {
      if (this.additionalTab === 1) {
        const defaultQueryParams = new URLSearchParams()
        defaultQueryParams.append('filter', 'all')
        const response = await this.getNotificationsGrouped(defaultQueryParams)

        const content = response.content
        const participantNotifications = content.filter(el => {
          return el.countUnread > 0 ? el : null
        })
        const participantProfiles = participantNotifications.map(
          el => el.profile,
        )
        this.$store.commit(
          'profilePreviewsStates/setProfilePreviews',
          participantProfiles,
          {
            root: true,
          },
        )
        this.$store.commit('userStates/updateNotifications', {
          content: participantNotifications,
        })
        this.tab = 0
      }
    },
  },
  computed: {
    ...mapState('userStates', [
      'notifications',
      'notificationsGroupedFirstRender',
    ]),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapGetters('userStates', ['getRolename']),
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    notificationsGrouped() {
      return this.notificationsGroupedFirstRender
    },
  },
  methods: {
    ...mapActions('userStates', [
      'readNotificationsAll',
      'readNotification',
      'getNotificationsGrouped',
    ]),
    closeDialog() {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    readAll() {
      if (this.isAdvisor) {
        this.readNotificationsAll({ isAdvisor: this.isAdvisor })
      } else {
        this.readNotificationsAll({ isAdvisor: false })
      }
    },
    loadNotificationsGrouped() {
      const content = this.notificationsGrouped.content
      const defaultQueryParams = new URLSearchParams()
      const unreadQueryParams = new URLSearchParams()
      unreadQueryParams.append('filter', 'unread')
      defaultQueryParams.append('filter', this.tab === 1 ? '3d' : '1d')

      if (this.tab !== 0) {
        return this.getNotificationsGrouped(defaultQueryParams)
      }
      if (this.additionalTab === 0 && this.tab === 0) {
        this.$store.commit('userStates/updateNotifications', {
          content,
        })
        const participantProfiles = content.map(el => el.profile)
        this.$store.commit(
          'profilePreviewsStates/setProfilePreviews',
          participantProfiles,
          {
            root: true,
          },
        )
      } else if (this.additionalTab === 1) {
        const participantNotifications = content.filter(el => {
          return el.countUnread > 0 ? el : null
        })
        const participantProfiles = participantNotifications.map(
          el => el.profile,
        )
        this.$store.commit(
          'profilePreviewsStates/setProfilePreviews',
          participantProfiles,
          {
            root: true,
          },
        )
        this.$store.commit('userStates/updateNotifications', {
          content: participantNotifications,
        })
      } else {
        return null
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.notifications_sidebar {
  &-dialog.v-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      display: flex;
      flex-direction: column;
    }
  }
  &-toolbar {
    flex: 0;
    & > div {
      padding: 6px 24px 0;
    }
    &-title {
      color: $bh-blue;
      font-size: 1.5715rem;
      font-weight: bold;
    }
    .readall {
      .v-icon {
        margin-right: 10px;
      }
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-gray--2;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
      z-index: 1;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
      flex-grow: 1;
      z-index: 1;
      overflow: auto;
      &-thumbnail {
        margin-top: 6px;
        border-radius: 8px;
        overflow: hidden;
        max-height: calc(100vh - 290px);
      }
    }
    &-footer {
      display: flex;
      padding: 12px 16px;
      z-index: 1;
      .v-icon {
        margin-right: 10px;
      }
    }
  }
}
.v-tabs__markAsRead {
  color: $bh-blue;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  width: 100%;
  border-radius: 0%;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  color: $bh-blue !important;
}
</style>
